import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import {
  AuthProvider as MetronAuthProvider,
  type AuthUser,
  useAuth as useMetronAuth,
} from '@metron-sweng/metron-auth/react';
import { createLogger, hasAdminAccess } from '@metron/shared';
import { authConfig } from '../config/auth.ts';

const logger = createLogger('AuthContext');

// Re-export AuthUser type for consumers
export type { AuthUser };

// Alias for backward compatibility
export type User = AuthUser;

/**
 * Extended auth context with wellspring-specific convenience methods
 */
interface WellspringAuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  hasGroup: (group: string) => boolean;
  login: () => void;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const WellspringAuthContext = createContext<WellspringAuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Internal wrapper that adds wellspring-specific context values
 */
function WellspringAuthWrapper({ children }: { children: ReactNode }) {
  const auth = useMetronAuth();

  const isAdmin = useMemo(() => {
    return hasAdminAccess(auth.user?.groups ?? []);
  }, [auth.user]);

  const hasGroup = useCallback(
    (group: string) => {
      return auth.user?.groups.includes(group) || false;
    },
    [auth.user],
  );

  const value = useMemo(
    () => ({
      ...auth,
      isAdmin,
      hasGroup,
    }),
    [auth, isAdmin, hasGroup],
  );

  return <WellspringAuthContext.Provider value={value}>{children}</WellspringAuthContext.Provider>;
}

/**
 * Auth provider that wraps metron-auth with wellspring-specific extensions
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const handleAuthStateChange = useCallback((user: AuthUser | null) => {
    if (user) {
      logger.info(
        { userEmail: user.email, groups: user.groups, action: 'session_valid' },
        'Valid session found',
      );
    } else {
      logger.info({ action: 'no_session' }, 'No valid session found');
    }
  }, []);

  return (
    <MetronAuthProvider config={authConfig} onAuthStateChange={handleAuthStateChange}>
      <WellspringAuthWrapper>{children}</WellspringAuthWrapper>
    </MetronAuthProvider>
  );
}

/**
 * Hook to access authentication state and methods
 *
 * Includes wellspring-specific convenience methods:
 * - isAdmin: Whether the user holds an Admin-tier group (Admin or SuperAdmin)
 * - hasGroup: Check if user belongs to a specific group
 */
export function useAuth(): WellspringAuthContextType {
  const context = useContext(WellspringAuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
